import useParameterStore from "../../store/parameterStore";
import { getPlacedPCell } from "../store/placedPCellStore";
import {
  applyPCellParameterUpdates,
  propagateMappedParameterChanges,
} from "./parametricMappingService";

const clone = (value) =>
  value && typeof value === "object"
    ? JSON.parse(JSON.stringify(value))
    : value;

const SOURCE_TYPE_MAP = {
  I: "int",
  F: "float",
  B: "binary",
  S: "string",
  LPP: "layer",
};

const getTargetParameterName = (parameter = {}) =>
  parameter.originalName || parameter.name || "";

const getMappingKey = (cellName, parameterName) =>
  `${cellName}:${parameterName}`;

const resolveActionCellName = (action, selectedCellName) => {
  const selected = String(selectedCellName || "").trim();
  const requested = String(action.cellName || selected).trim();
  if (!selected || requested.toLowerCase() !== selected.toLowerCase()) {
    throw new Error("AI actions may only target the currently selected PCell.");
  }
  return selected;
};

const normalizeScope = (scope) => {
  const normalized = String(scope || "").trim().toLowerCase();
  if (normalized !== "global" && normalized !== "local") {
    throw new Error("AI action has an invalid parameter scope.");
  }
  return normalized;
};

const isCompatibleType = (sourceType, targetType) => {
  const normalizedSource = SOURCE_TYPE_MAP[sourceType] || sourceType;
  return (
    normalizedSource === targetType ||
    (normalizedSource === "int" && targetType === "float") ||
    (normalizedSource === "float" && targetType === "int")
  );
};

const validateDesignValue = (type, value, name) => {
  if (type === "LPP") {
    if (
      !value ||
      typeof value !== "object" ||
      !String(value.layer || "").trim() ||
      !String(value.datatype || "").trim()
    ) {
      throw new Error(
        `Layer parameter "${name}" requires a layer and datatype.`
      );
    }
    return clone(value);
  }

  const text = String(value ?? "").trim();
  if (!text) throw new Error(`Parameter "${name}" requires a value.`);

  if (type === "I" && !/^-?\d+$/.test(text)) {
    throw new Error(`Integer parameter "${name}" requires a whole number.`);
  }
  if (type === "F" && !/^-?(?:\d+\.\d+|\.\d+)$/.test(text)) {
    throw new Error(`Float parameter "${name}" requires a decimal value.`);
  }
  if (type === "B" && !/^(0|1|T|nil)$/i.test(text)) {
    throw new Error(`Binary parameter "${name}" allows only 0, 1, T or nil.`);
  }
  if (type === "S" && !/^[A-Za-z0-9_]+$/.test(text)) {
    throw new Error(
      `String parameter "${name}" allows letters, numbers and underscores only.`
    );
  }

  return text;
};

const coercePCellValue = (parameter, value) => {
  if (parameter.type === "int") {
    const number = Number(value);
    if (!Number.isInteger(number)) {
      throw new Error(`Parameter "${getTargetParameterName(parameter)}" requires an integer.`);
    }
    return number;
  }

  if (parameter.type === "float") {
    const number = Number(value);
    if (!Number.isFinite(number)) {
      throw new Error(`Parameter "${getTargetParameterName(parameter)}" requires a number.`);
    }
    return number;
  }

  if (parameter.type === "binary") {
    if (value === true || value === 1 || value === "1" || String(value).toUpperCase() === "T") {
      return 1;
    }
    if (value === false || value === 0 || value === "0" || String(value).toLowerCase() === "nil") {
      return 0;
    }
    throw new Error(`Parameter "${getTargetParameterName(parameter)}" requires 0 or 1.`);
  }

  if (parameter.type === "string") return String(value);
  if (parameter.type === "layer" && value && typeof value === "object") {
    return clone(value);
  }

  throw new Error(
    `AI cannot safely update parameter "${getTargetParameterName(parameter)}".`
  );
};

const createParameterId = (scope, index) =>
  `param_${scope}_${Date.now()}_${index}_${Math.random().toString(16).slice(2)}`;

const findParameter = (rows, action) => {
  const id = String(action.parameterId || "");
  const name = String(action.parameterName || "").trim().toLowerCase();

  return rows.find(
    (parameter) =>
      (id && parameter.id === id) ||
      (name && String(parameter.name || "").trim().toLowerCase() === name)
  );
};

const assertUniqueParameterName = (globalRows, localRows, name) => {
  const key = String(name || "").trim().toLowerCase();
  if (
    [...globalRows, ...localRows].some(
      (parameter) => String(parameter.name || "").trim().toLowerCase() === key
    )
  ) {
    throw new Error(`Parameter name "${name}" already exists.`);
  }
};

const addPCellUpdate = (updates, cellName, parameterName, value) => {
  const key = getMappingKey(cellName, parameterName);
  updates.set(key, { cellName, parameterName, value });
};

export const executeParametricAiActions = async ({
  actions,
  selectedCellName,
}) => {
  if (!Array.isArray(actions) || actions.length === 0) {
    throw new Error("The AI proposal has no executable actions.");
  }

  const store = useParameterStore.getState();
  const snapshot = {
    globalParameters: clone(store.globalParameters),
    localParameters: clone(store.localParameters),
    parameterMappings: clone(store.parameterMappings),
  };
  const nextGlobal = clone(store.globalParameters);
  const nextLocal = clone(store.localParameters);
  const nextMappings = clone(store.parameterMappings);
  const changedSourceIds = { global: new Set(), local: new Set() };
  const pcellUpdates = new Map();
  const affectedCellNames = new Set();

  actions.forEach((action, actionIndex) => {
    const actionName = String(action?.action || "").trim();

    if (actionName === "create_parameter") {
      const scope = normalizeScope(action.scope);
      const name = String(action.parameterName || "").trim();
      const type = String(action.parameterType || "").trim().toUpperCase();
      if (!/^[A-Za-z_][A-Za-z0-9_ ]*$/.test(name)) {
        throw new Error(`Parameter name "${name}" is invalid.`);
      }
      if (!["I", "F", "B", "S", "LPP"].includes(type)) {
        throw new Error(`Parameter "${name}" has an invalid datatype.`);
      }
      assertUniqueParameterName(nextGlobal, nextLocal, name);
      const value = validateDesignValue(type, action.value, name);
      const parameter = {
        id: createParameterId(scope, actionIndex),
        name,
        type,
        defaultValue: value,
        groupName: String(action.groupName || "").trim(),
        ranges: [],
      };
      (scope === "global" ? nextGlobal : nextLocal).push(parameter);
      return;
    }

    if (actionName === "update_parameter") {
      const scope = normalizeScope(action.scope);
      const rows = scope === "global" ? nextGlobal : nextLocal;
      const parameter = findParameter(rows, action);
      if (!parameter) {
        throw new Error(`Parameter "${action.parameterName}" is no longer available.`);
      }
      if (
        Object.prototype.hasOwnProperty.call(action, "expectedValue") &&
        String(parameter.defaultValue ?? "") !== String(action.expectedValue ?? "")
      ) {
        throw new Error(
          `Parameter "${parameter.name}" changed after this proposal was generated. Please send the instruction again.`
        );
      }
      parameter.defaultValue = validateDesignValue(
        parameter.type,
        action.value,
        parameter.name
      );
      changedSourceIds[scope].add(parameter.id);
      return;
    }

    if (actionName === "map_parameter_bulk") {
      const sourceScope = normalizeScope(action.sourceScope);
      const sourceRows = sourceScope === "global" ? nextGlobal : nextLocal;
      const source =
        sourceRows.find((parameter) => parameter.id === action.sourceId) ||
        sourceRows.find(
          (parameter) =>
            String(parameter.name || "").trim().toLowerCase() ===
            String(action.sourceParameter || "").trim().toLowerCase()
        );
      if (!source) {
        throw new Error(
          `Mapping source "${action.sourceParameter}" is no longer available.`
        );
      }

      const targets = Array.isArray(action.targets) ? action.targets : [];
      if (!targets.length) {
        throw new Error("The bulk mapping proposal has no valid PCell targets.");
      }

      const seenTargets = new Set();
      targets.forEach((target) => {
        const cellName = String(target?.cellName || "").trim();
        const parameterName = String(target?.targetParameter || "").trim();
        const mappingKey = getMappingKey(cellName, parameterName);

        if (!cellName || !parameterName || seenTargets.has(mappingKey)) {
          throw new Error("The bulk mapping proposal contains an invalid target.");
        }
        seenTargets.add(mappingKey);

        const placed = getPlacedPCell(cellName);
        if (!placed) throw new Error(`PCell "${cellName}" is no longer available.`);
        const targetParameter = (placed.parameters || []).find(
          (parameter) => getTargetParameterName(parameter) === parameterName
        );
        if (!targetParameter) {
          throw new Error(
            `Parameter "${parameterName}" is no longer available on ${cellName}.`
          );
        }
        if (!isCompatibleType(source.type, targetParameter.type)) {
          throw new Error(
            `Parameter "${source.name}" cannot be mapped to "${cellName}.${parameterName}" because their datatypes are incompatible.`
          );
        }

        const existingMapping = nextMappings[mappingKey];
        if (
          Object.prototype.hasOwnProperty.call(target, "expectedSourceId") &&
          String(existingMapping?.sourceId || "") !==
            String(target.expectedSourceId || "")
        ) {
          throw new Error(
            `Mapping for "${cellName}.${parameterName}" changed after this proposal was generated. Please send the instruction again.`
          );
        }

        nextMappings[mappingKey] = {
          sourceScope,
          sourceId: source.id,
          sourceName: source.name,
          manualValue: Object.prototype.hasOwnProperty.call(
            existingMapping || {},
            "manualValue"
          )
            ? clone(existingMapping.manualValue)
            : clone(targetParameter.value),
        };
        affectedCellNames.add(cellName);
      });

      changedSourceIds[sourceScope].add(source.id);
      return;
    }

    if (actionName === "map_parameter") {
      const sourceScope = normalizeScope(action.sourceScope);
      const sourceRows = sourceScope === "global" ? nextGlobal : nextLocal;
      const source =
        sourceRows.find((parameter) => parameter.id === action.sourceId) ||
        sourceRows.find(
          (parameter) =>
            String(parameter.name || "").trim().toLowerCase() ===
            String(action.sourceParameter || "").trim().toLowerCase()
        );
      if (!source) {
        throw new Error(`Mapping source "${action.sourceParameter}" is no longer available.`);
      }

      const cellName = resolveActionCellName(action, selectedCellName);
      const placed = getPlacedPCell(cellName);
      if (!placed) throw new Error(`PCell "${cellName}" is no longer available.`);
      const targetParameter = (placed.parameters || []).find(
        (parameter) =>
          getTargetParameterName(parameter) === action.targetParameter
      );
      if (!targetParameter) {
        throw new Error(
          `Parameter "${action.targetParameter}" is no longer available on ${cellName}.`
        );
      }
      if (!isCompatibleType(source.type, targetParameter.type)) {
        throw new Error(
          `Parameter "${source.name}" cannot be mapped to "${action.targetParameter}" because their datatypes are incompatible.`
        );
      }

      const mappingKey = getMappingKey(cellName, action.targetParameter);
      const existingMapping = nextMappings[mappingKey];
      if (
        Object.prototype.hasOwnProperty.call(action, "expectedSourceId") &&
        String(existingMapping?.sourceId || "") !==
          String(action.expectedSourceId || "")
      ) {
        throw new Error(
          `Mapping for "${action.targetParameter}" changed after this proposal was generated. Please send the instruction again.`
        );
      }
      nextMappings[mappingKey] = {
        sourceScope,
        sourceId: source.id,
        sourceName: source.name,
        manualValue: Object.prototype.hasOwnProperty.call(
          existingMapping || {},
          "manualValue"
        )
          ? clone(existingMapping.manualValue)
          : clone(targetParameter.value),
      };
      changedSourceIds[sourceScope].add(source.id);
      affectedCellNames.add(cellName);
      return;
    }

    if (actionName === "remove_mapping") {
      const cellName = resolveActionCellName(action, selectedCellName);
      const mappingKey = getMappingKey(cellName, action.targetParameter);
      const existingMapping = nextMappings[mappingKey];
      if (!existingMapping) {
        throw new Error(`Parameter "${action.targetParameter}" is no longer mapped.`);
      }
      if (
        action.expectedSourceId &&
        existingMapping.sourceId !== action.expectedSourceId
      ) {
        throw new Error(
          `Mapping for "${action.targetParameter}" changed after this proposal was generated. Please send the instruction again.`
        );
      }

      delete nextMappings[mappingKey];
      affectedCellNames.add(cellName);
      if (Object.prototype.hasOwnProperty.call(existingMapping, "manualValue")) {
        addPCellUpdate(
          pcellUpdates,
          cellName,
          action.targetParameter,
          clone(existingMapping.manualValue)
        );
      }
      return;
    }

    if (actionName === "update_pcell_parameter") {
      const cellName = resolveActionCellName(action, selectedCellName);
      const mappingKey = getMappingKey(cellName, action.targetParameter);
      if (nextMappings[mappingKey]) {
        throw new Error(
          `Parameter "${action.targetParameter}" is mapped and must be changed through its Global or Local source.`
        );
      }
      const placed = getPlacedPCell(cellName);
      if (!placed) throw new Error(`PCell "${cellName}" is no longer available.`);
      const parameter = (placed.parameters || []).find(
        (item) => getTargetParameterName(item) === action.targetParameter
      );
      if (!parameter) {
        throw new Error(
          `Parameter "${action.targetParameter}" is no longer available on ${cellName}.`
        );
      }
      if (
        Object.prototype.hasOwnProperty.call(action, "expectedValue") &&
        String(parameter.value ?? "") !== String(action.expectedValue ?? "")
      ) {
        throw new Error(
          `Parameter "${action.targetParameter}" changed after this proposal was generated. Please send the instruction again.`
        );
      }
      addPCellUpdate(
        pcellUpdates,
        cellName,
        action.targetParameter,
        coercePCellValue(parameter, action.value)
      );
      affectedCellNames.add(cellName);
      return;
    }

    throw new Error(`Unsupported AI action "${actionName}".`);
  });

  const setSessionState = store.setParameterSessionState;
  setSessionState({
    globalParameters: nextGlobal,
    localParameters: nextLocal,
    parameterMappings: nextMappings,
  });

  try {
    for (const scope of ["global", "local"]) {
      if (!changedSourceIds[scope].size) continue;
      propagateMappedParameterChanges({
        sourceScope: scope,
        changedSourceIds: Array.from(changedSourceIds[scope]),
        globalParameters: nextGlobal,
        localParameters: nextLocal,
        parameterMappings: nextMappings,
      });
    }

    if (pcellUpdates.size) {
      applyPCellParameterUpdates(Array.from(pcellUpdates.values()));
    }
  } catch (error) {
    setSessionState(snapshot);
    throw error;
  }

  return {
    actionCount: actions.length,
    affectedCells: Array.from(
      new Set([
        ...affectedCellNames,
        ...Array.from(pcellUpdates.values()).map((item) => item.cellName),
        ...actions
          .filter((action) => action.action !== "map_parameter_bulk")
          .map((action) => action.cellName)
          .filter(Boolean),
      ])
    ),
  };
};
